// api/engine-slates.ts — (TF-6b-3) Admin → Slates: /api/admin/slates/*, the backend's proxy to ingest's slate routes.
import { apiGet, apiPost, apiPatch } from "./client.ts";
import type { SlateView } from "@/lib/slates.ts";
import type { SlateList, SlateEstimate, SlateApproved, SlateApprovePartial } from "@vms/contracts";
import { dryRunField } from "@/lib/dry-run.ts";
import type { LearningView } from "@/lib/learning.ts";

export interface SlateCreateBody {
  operation: "generate" | "develop"; primaryPlatform: "tiktok" | "youtube"; audienceId: string | null;
  count?: number | null; prompt?: string | null; seeds?: string[]; ideas?: { text: string }[];
  /** (tf3-40; C2.1-1) the audience-first slate: Generate with an audience, no count and no typed ideas */ audienceFirst?: boolean;
}
export type SlateEstimateAnswer = SlateEstimate;   // (B64) contracts' answers
export type SlateApproveAnswer = SlateApproved | SlateApprovePartial;

const P = "/admin/slates";
export const engineSlatesApi = {
  list: () => apiGet<SlateList>(P),   // (B64) contracts' SlateList
  create: (b: SlateCreateBody) => apiPost<{ slateId: string; runId: string; status: string }>(P, b),
  get: (id: string) => apiGet<SlateView>(`${P}/${id}`),
  decide: (id: string, ideaId: string, b: { action: "keep" | "edit" | "kill"; title?: string; premise?: string; hookFirstLine?: string }) =>
    apiPatch<{ status: string }>(`${P}/${id}/ideas/${ideaId}`, b),
  // (tf3-35b) `dry`: an admin's dry run — each pilot (and the estimate) carries it
  estimate: (id: string, ideaIds: string[], castRef: string | null, dry = false) => apiPost<SlateEstimateAnswer>(`${P}/${id}/estimate`, { ideaIds, castRef, ...dryRunField(dry) }),
  approve: (id: string, ideaIds: string[], capUsdCents: number, castRef: string | null, dry = false) =>
    apiPost<SlateApproveAnswer>(`${P}/${id}/approve`, { ideaIds, capUsdCents, castRef, ...dryRunField(dry) }),
  // (tf3-40; C2.1-1) the audience-first slate's two reviews
  interests: (id: string, b: { keep: string[]; add: { interest: string; reason: string }[]; reason?: string }) =>
    apiPost<{ slateId: string; review: "interests"; kept: string[] }>(`${P}/${id}/interests`, b),
  strategies: (id: string, b: { choose: string[]; reason?: string }) =>
    apiPost<{ slateId: string; review: "strategies"; kept: string[] }>(`${P}/${id}/strategies`, b),
  // (tf3-41; C2.2-2) a pilot made by hand, linked to its scored card
  link: (id: string, ideaId: string, runId: string, reason?: string) =>
    apiPost<{ slateId: string; ideaId: string; runId: string; status: "linked" }>(`${P}/${id}/ideas/${ideaId}/link`, { runId, ...(reason?.trim() ? { reason: reason.trim() } : {}) }),
  close: (id: string, reason: string) => apiPost<{ status: string }>(`${P}/${id}/close`, { reason }),
  // (tf3-42) each with its MERGED age band: an audience of children is disabled on New slate
  audiences: () => apiGet<{ profiles: { id: string; label: string; resolvedAgeBand?: string | null }[] }>("/admin/audiences"),
  // (tf3-42; C2.2-1) what the slates learned for an audience (none: the "no audience" bucket)
  learning: (audienceId: string | null) => apiGet<LearningView>(`${P}/learning${audienceId ? `?audienceId=${encodeURIComponent(audienceId)}` : ""}`),
};
